import { createFileRoute } from "@tanstack/react-router";
import { ColorIdToColor } from "common/materials/library/colorIdToColor";
import { DM_Grass_Lawn_01, DM_OUTDATED_MATERIAL } from "common/materials/library/designMaterialPath";
import { LayerConfigIdToJson } from "common/materials/library/layerConfigIdToJson";
import { MaterialTemplateIdToJson } from "common/materials/library/materialTemplateIdToJson";
import { PropLegacyMaterialIdToSrc } from "common/materials/library/propLegacyMaterialId";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey, materialSrcFromCacheKey } from "common/materials/materialSrc";
import {
  materialSrcFromPathString,
  materialTemplateIdFromName,
  pathStringFromMaterialSrc,
} from "common/materials/materialSrcFromPathString";
import {
  cachedMaterialThumbnailWidth,
  cachedMaterialThumbnailWorldWidth,
  materialThumbnailCacheDevSuffix,
  currentMaterialThumbnailCacheId,
  getMaterialThumbnailAwsPath,
} from "common/materials/materialThumbnailCachePaths";
import { defaultThumbnailLightOptions } from "gfx/materials/designMaterialRenderImage";
import { getMaterialPathAutofillOptions } from "lib/components/studio/inspector/paletteMaterialUtils";
import { useRenderedMaterialPreview } from "lib/hooks/useRenderedMaterialPreview";
import { getPostedFileURLByName } from "lib/storage/fileStorage";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";

const defaultMaterialSrc: MaterialSrc = DM_Grass_Lawn_01.Default;
const defaultMaterialPath: string = pathStringFromMaterialSrc(defaultMaterialSrc);

function materialSrcToLegacyId(materialSrc: MaterialSrc): string {
  for (const legacyId of Object.keys(PropLegacyMaterialIdToSrc)) {
    const idSrc = PropLegacyMaterialIdToSrc[legacyId];
    if (
      idSrc.templateId === materialSrc.templateId &&
      idSrc.layerConfigId === materialSrc.layerConfigId &&
      idSrc.colorId === materialSrc.colorId
    ) {
      return legacyId;
    }
  }
  return "";
}

function MaterialAppearanceTestPage() {
  const [validMaterialSrc, setValidMaterialSrc] = useState<MaterialSrc | undefined>(defaultMaterialSrc);
  const [materialPath, setMaterialPath] = useState<string>(defaultMaterialPath);
  const [legacyMaterialId, setLegacyMaterialId] = useState<string>(materialSrcToLegacyId(defaultMaterialSrc));
  const [materialCacheId, setMaterialCacheId] = useState<string>(materialCacheKey(defaultMaterialSrc));
  const [materialCacheIdValid, setMaterialCacheIdValid] = useState<boolean>(true);
  const [materialThumbnailAwsUrl, setMaterialThumbnailAwsUrl] = useState<string>("");
  const [materialThumbnailAwsDevUrl, setMaterialThumbnailAwsDevUrl] = useState<string>("");
  const [templateId, setTemplateId] = useState<number | undefined>(defaultMaterialSrc.templateId);
  const [templateName, setTemplateName] = useState<string | undefined>(TemplateIdToName[defaultMaterialSrc.templateId]);
  const [layerConfigId, setLayerConfigId] = useState<number | undefined>(defaultMaterialSrc.layerConfigId);
  const [colorId, setColorId] = useState<number | undefined>(defaultMaterialSrc.colorId);

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const thumbnailOptions = {
    ...defaultThumbnailLightOptions,
    width: cachedMaterialThumbnailWidth,
    height: cachedMaterialThumbnailWidth,
    worldWidth: cachedMaterialThumbnailWorldWidth,
  };
  useRenderedMaterialPreview(validMaterialSrc, previewCanvasRef, thumbnailOptions);

  useEffect(() => {
    const materialSrc = materialSrcFromPathString(materialPath);
    if (materialSrc) {
      setMaterialCacheId(materialCacheKey(materialSrc));
      setLegacyMaterialId(materialSrcToLegacyId(materialSrc));
      setTemplateId(materialSrc.templateId);
      setTemplateName(TemplateIdToName[materialSrc.templateId]);
      setValidMaterialSrc(materialSrc);
      if (materialSrc.layerConfigId !== undefined) {
        setLayerConfigId(materialSrc.layerConfigId);
      } else {
        setLayerConfigId(undefined);
      }
      if (materialSrc.colorId !== undefined) {
        setColorId(materialSrc.colorId);
      } else {
        setColorId(undefined);
      }
      const awsPath = getMaterialThumbnailAwsPath(
        currentMaterialThumbnailCacheId,
        materialSrc,
        cachedMaterialThumbnailWidth,
        cachedMaterialThumbnailWidth,
      );
      const devAwsPath = getMaterialThumbnailAwsPath(
        currentMaterialThumbnailCacheId + materialThumbnailCacheDevSuffix,
        materialSrc,
        cachedMaterialThumbnailWidth,
        cachedMaterialThumbnailWidth,
      );
      void (async () => {
        const url = await getPostedFileURLByName(awsPath);
        if (url) {
          setMaterialThumbnailAwsUrl(url);
        }
        const devUrl = await getPostedFileURLByName(devAwsPath);
        if (devUrl) {
          setMaterialThumbnailAwsDevUrl(devUrl);
        }
      })();
    } else {
      setMaterialCacheId("");
      setLegacyMaterialId("");
      setTemplateId(undefined);
      setValidMaterialSrc(undefined);
      setLayerConfigId(undefined);
      setColorId(undefined);
    }
  }, [materialPath]);

  function trySetMaterialSrc(materialSrc: MaterialSrc) {
    const parsedMaterialPath = pathStringFromMaterialSrc(materialSrc);
    const isOutdatedMaterial =
      DM_OUTDATED_MATERIAL.Default.templateId === materialSrc.templateId &&
      materialSrc.colorId === undefined &&
      materialSrc.layerConfigId === undefined;
    const srcFromPath = materialSrcFromPathString(parsedMaterialPath);
    if (
      isOutdatedMaterial ||
      (srcFromPath &&
        !parsedMaterialPath.startsWith("OUTDATED_MATERIAL") &&
        srcFromPath.templateId === materialSrc.templateId &&
        srcFromPath.colorId === materialSrc.colorId &&
        srcFromPath.layerConfigId === materialSrc.layerConfigId)
    ) {
      setValidMaterialSrc(materialSrc);
      setMaterialCacheId(materialCacheKey(materialSrc));
      setMaterialPath(parsedMaterialPath);
      setLegacyMaterialId(materialSrcToLegacyId(materialSrc));
    } else {
      setValidMaterialSrc(undefined);
    }
  }

  function handleTemplateIdChange(stringValue) {
    if (stringValue === "") {
      setTemplateId(undefined);
      setTemplateName("");
      setValidMaterialSrc(undefined);
      return;
    }
    const parsedTemplateId = parseInt(stringValue);
    if (!isNaN(parsedTemplateId)) {
      setTemplateId(parsedTemplateId);
      if (parsedTemplateId >= 0 && parsedTemplateId < MaterialTemplateIdToJson.length) {
        const materialSrc: MaterialSrc = {
          templateId: parsedTemplateId,
          colorId,
          layerConfigId,
        };
        trySetMaterialSrc(materialSrc);

        // Check if the template has been merged to another template.
        const parsedTemplateName = TemplateIdToName[parsedTemplateId];
        const mergedTemplateId = materialTemplateIdFromName(parsedTemplateName);
        if (mergedTemplateId !== materialSrc.templateId && mergedTemplateId !== 108) {
          setTemplateName(parsedTemplateName + " merged to templateId " + mergedTemplateId);
        } else {
          setTemplateName(parsedTemplateName);
        }
      } else {
        setValidMaterialSrc(undefined);
      }
    }
  }

  function handleLayerConfigIdChange(stringValue) {
    let parsedLayerConfigId: number | undefined;
    if (stringValue === "") {
      parsedLayerConfigId = undefined;
    } else {
      parsedLayerConfigId = parseInt(stringValue);
      if (isNaN(parsedLayerConfigId)) {
        setValidMaterialSrc(undefined);
        return;
      }
    }
    setLayerConfigId(parsedLayerConfigId);
    if (
      templateId &&
      (parsedLayerConfigId === undefined ||
        (parsedLayerConfigId >= 0 && parsedLayerConfigId < LayerConfigIdToJson.length))
    ) {
      const materialSrc: MaterialSrc = {
        templateId,
        colorId,
        layerConfigId: parsedLayerConfigId,
      };
      trySetMaterialSrc(materialSrc);
    } else {
      setValidMaterialSrc(undefined);
    }
  }

  function handleColorIdChange(stringValue) {
    let parsedColorId: number | undefined;
    if (stringValue === "") {
      parsedColorId = undefined;
    } else {
      parsedColorId = parseInt(stringValue);
      if (isNaN(parsedColorId)) {
        setValidMaterialSrc(undefined);
        return;
      }
    }
    setColorId(parsedColorId);
    if (templateId && (parsedColorId === undefined || (parsedColorId >= 0 && parsedColorId < ColorIdToColor.length))) {
      const materialSrc: MaterialSrc = {
        templateId,
        colorId: parsedColorId,
        layerConfigId,
      };
      trySetMaterialSrc(materialSrc);
    } else {
      setValidMaterialSrc(undefined);
    }
  }

  function handleCacheIdChange(stringValue: string) {
    setMaterialCacheId(stringValue);
    if (!/^\d+_\d*_\d*$/.test(stringValue.trim())) {
      setMaterialCacheIdValid(false);
      return;
    }
    const materialSrc = materialSrcFromCacheKey(stringValue);
    setTemplateId(materialSrc.templateId);
    setLayerConfigId(materialSrc.layerConfigId);
    setColorId(materialSrc.colorId);
    setMaterialCacheIdValid(true);
    trySetMaterialSrc(materialSrc);
  }

  function handleLegacyMaterialIdChange(stringValue: string) {
    setLegacyMaterialId(stringValue);
    if (PropLegacyMaterialIdToSrc.hasOwnProperty(stringValue)) {
      const materialSrc = PropLegacyMaterialIdToSrc[stringValue];
      trySetMaterialSrc(materialSrc);
    }
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Material Id Tools")}</title>
        <meta name="robots" content="noindex" />

        <style>{`
          .field {
            margin-bottom: 20px;
          }

          input[type=button] {
              display: inline-block;
              padding: 10px;
          }

          canvas.renderedPreview {
            display: block;
          }

          .invalid {
            color: #ff0000;
          }

          select {
            color: black;
          }

          .two-columns {
              display:flex;
              flex-wrap:wrap;
          }

          .two-columns > * {
              width:100%;
              padding: 20px;
          }

          @media (min-width:700px) {
              .two-columns > * {
                  width:50%;
              }
          }
        `}</style>
      </Head>

      <div className="two-columns">
        <div>
          <div className="field">
            <div>Material path: {materialPath}</div>
            <Select
              value=""
              placeholder="Material ID"
              options={(input) => getMaterialPathAutofillOptions(input, true)}
              labelFromValue={(value) => value as string}
              onChange={(value) => {
                setMaterialPath(value as string);
              }}
            />
          </div>
          <div className={"field " + (validMaterialSrc ? "valid" : "invalid")}>
            Template id: {templateName}
            <Input
              type="number"
              value={templateId !== undefined ? templateId : ""}
              onChange={(ev) => handleTemplateIdChange(ev.target.value)}
            ></Input>
          </div>
          <div className={"field " + (validMaterialSrc ? "valid" : "invalid")}>
            Layer config id:
            <Input
              type="number"
              value={layerConfigId !== undefined ? layerConfigId : ""}
              onChange={(ev) => handleLayerConfigIdChange(ev.target.value)}
            ></Input>
          </div>
          <div className={"field " + (validMaterialSrc ? "valid" : "invalid")}>
            Color id:
            <Input
              type="number"
              value={colorId !== undefined ? colorId : ""}
              onChange={(ev) => handleColorIdChange(ev.target.value)}
            ></Input>
          </div>
          <div className={"field " + (materialCacheIdValid ? "valid" : "invalid")}>
            Cache id: <Input value={materialCacheId} onChange={(ev) => handleCacheIdChange(ev.target.value)}></Input>
          </div>
          <div className="field">
            Deprecated prop legacy material id (PropLegacyMaterialId):
            <Input value={legacyMaterialId} onChange={(ev) => handleLegacyMaterialIdChange(ev.target.value)}></Input>
          </div>
        </div>
        <div>
          <div className="field">
            Rendered thumbnail:
            <canvas ref={previewCanvasRef} className="renderedPreview"></canvas>
          </div>
          <div className="field">
            Cached Thumbnail URL (normal): <pre>{materialThumbnailAwsUrl}</pre>
            <img
              src={materialThumbnailAwsUrl}
              width={cachedMaterialThumbnailWidth}
              height={cachedMaterialThumbnailWidth}
            />
          </div>
          <div className="field">
            Cached Thumbnail URL (experimental development): <pre>{materialThumbnailAwsDevUrl}</pre>
            <img
              src={materialThumbnailAwsDevUrl}
              width={cachedMaterialThumbnailWidth}
              height={cachedMaterialThumbnailWidth}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/material-id")({
  component: withAuthentication(MaterialAppearanceTestPage, { superAdmin: true }),
});
